import {
	FORM_FILE_TYPE_GROUPS,
	type FormFieldDefinitionModel,
	type FormFieldErrorCode,
	type FormFileTypeGroup,
} from './form-types';

/**
 * PD-16713 — a file upload question (documents a visitor attaches to a submission), the
 * parts the page and the server share: the kinds of file accepted, the limits, the checks a
 * page runs before it uploads anything, telling a file's real kind from its first bytes,
 * and cleaning the name a visitor's computer gave it. Pure, so it runs unchanged in a
 * browser and in Node.
 *
 * How a file travels: the page uploads each file on its own as it is picked
 * (`POST /api/portal/forms/:formKey/uploads`, one file per request) with an upload id it
 * makes, and sends those ids as the question's answer when the form is submitted. The
 * server attaches the files to the submission then; an upload nothing attaches within
 * FORM_UPLOAD_UNCLAIMED_HOURS is deleted.
 *
 * Malware: there is no scanner yet (owner decision, 2026-10-01). Only PDF, JPEG, PNG and
 * WebP are accepted, judged by the content rather than the name, and pictures are
 * re-encoded on the server so only a clean copy is ever stored. The website forms code
 * (modules/websites site-blocks/workd-forms.ts) mirrors the page-side parts of this file,
 * which it cannot import; a parity test runs the same cases through both.
 */

/** A kind of file Workd accepts, as decided from its content. */
export type FormFileFormat = 'pdf' | 'jpeg' | 'png' | 'webp';

export interface FormFileFormatModel {
	format: FormFileFormat;
	group: FormFileTypeGroup;
	/** The type the file is stored and served as. */
	contentType: string;
	/** Lowercase, with the dot; the first is the one a cleaned name is given. */
	extensions: ReadonlyArray<string>;
	/** How the kind is named to people. */
	label: string;
}

export const FORM_FILE_FORMATS: ReadonlyArray<FormFileFormatModel> = [
	{
		format: 'pdf',
		group: 'pdf',
		contentType: 'application/pdf',
		extensions: ['.pdf'],
		label: 'PDF',
	},
	{
		format: 'jpeg',
		group: 'image',
		contentType: 'image/jpeg',
		extensions: ['.jpg', '.jpeg'],
		label: 'JPEG',
	},
	{
		format: 'png',
		group: 'image',
		contentType: 'image/png',
		extensions: ['.png'],
		label: 'PNG',
	},
	{
		format: 'webp',
		group: 'image',
		contentType: 'image/webp',
		extensions: ['.webp'],
		label: 'WebP',
	},
];

/** The largest file a question takes unless its admin chose otherwise, in megabytes. */
export const FORM_FILE_DEFAULT_MAX_MB = 10;
/** The range an admin may choose a question's largest file from, in megabytes. */
export const FORM_FILE_MIN_MB = 1;
export const FORM_FILE_MAX_MB = 25;

/** The most files a question takes unless its admin chose otherwise. */
export const FORM_FILE_DEFAULT_MAX_FILES = 5;
/** The range an admin may choose a question's most files from. */
export const FORM_FILE_MIN_FILES = 1;
export const FORM_FILE_MAX_FILES = 10;

/**
 * The upload endpoint's own ceiling, in bytes: the largest any question can allow. The
 * question's smaller limit is checked after the file arrives.
 */
export const FORM_FILE_UPLOAD_MAX_BYTES = FORM_FILE_MAX_MB * 1024 * 1024;

/** A stored file name is at most this long (the `_file` name column). */
export const FORM_FILE_NAME_MAX_LENGTH = 255;

/** An upload no submission has taken within this many hours is deleted. */
export const FORM_UPLOAD_UNCLAIMED_HOURS = 24;

/** An upload id: a UUID the page makes for each file it uploads. */
export const FORM_UPLOAD_ID_PATTERN =
	/^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i;

export function isFormUploadId(value: unknown): value is string {
	return typeof value === 'string' && FORM_UPLOAD_ID_PATTERN.test(value);
}

/**
 * A file as a page sees it before uploading (a browser `File` fits). Declared here because
 * this library is built without the browser's types.
 */
export interface FormFileCandidateModel {
	name: string;
	/** Bytes. */
	size: number;
	/** The type the browser reports; often empty. */
	type: string;
}

/** What a file question takes, with every default and range applied. */
export interface FormFileLimitsModel {
	types: Array<FormFileTypeGroup>;
	maxMb: number;
	maxBytes: number;
	maxFiles: number;
}

function wholeNumberIn(
	value: unknown,
	min: number,
	max: number,
	fallback: number,
): number {
	return typeof value === 'number' &&
		Number.isInteger(value) &&
		value >= min &&
		value <= max
		? value
		: fallback;
}

/**
 * A file question's limits. Read forgivingly, since a page draws whatever it is sent: an
 * unknown kind is ignored, no kinds at all means every kind, and a size or count outside
 * the allowed range falls back to the default. The server refuses such values when the
 * form is saved (checkFormDefinition), so this only matters for data it never checked.
 */
export function formFileLimits(
	field: Pick<FormFieldDefinitionModel, 'validationRules'>,
): FormFileLimitsModel {
	const rules = field.validationRules ?? {};
	const chosen: ReadonlyArray<unknown> = Array.isArray(rules.fileTypes)
		? rules.fileTypes
		: [];
	const types = FORM_FILE_TYPE_GROUPS.filter((group) =>
		chosen.includes(group),
	);
	const maxMb = wholeNumberIn(
		rules.maxFileSizeMb,
		FORM_FILE_MIN_MB,
		FORM_FILE_MAX_MB,
		FORM_FILE_DEFAULT_MAX_MB,
	);
	return {
		types: types.length > 0 ? types : [...FORM_FILE_TYPE_GROUPS],
		maxMb,
		maxBytes: maxMb * 1024 * 1024,
		maxFiles: wholeNumberIn(
			rules.maxFiles,
			FORM_FILE_MIN_FILES,
			FORM_FILE_MAX_FILES,
			FORM_FILE_DEFAULT_MAX_FILES,
		),
	};
}

/** The formats a set of kinds covers, in the order FORM_FILE_FORMATS lists them. */
export function formFileFormatsFor(
	types: ReadonlyArray<FormFileTypeGroup>,
): Array<FormFileFormatModel> {
	return FORM_FILE_FORMATS.filter((format) => types.includes(format.group));
}

/** The file chooser's `accept`: each format's extensions and its type. */
export function formFileAccept(
	types: ReadonlyArray<FormFileTypeGroup>,
): string {
	return formFileFormatsFor(types)
		.flatMap((format) => [...format.extensions, format.contentType])
		.join(',');
}

/** "PDF, JPEG, PNG or WebP": the kinds a question takes, as people read them. */
export function formFileTypeWords(
	types: ReadonlyArray<FormFileTypeGroup>,
): string {
	const labels = formFileFormatsFor(types).map((format) => format.label);
	if (labels.length <= 1) {
		return labels[0] ?? '';
	}
	return `${labels.slice(0, -1).join(', ')} or ${labels[labels.length - 1]}`;
}

export function formFileFormat(format: FormFileFormat): FormFileFormatModel {
	const found = FORM_FILE_FORMATS.find((entry) => entry.format === format);
	if (found == null) {
		// Unreachable: every FormFileFormat is listed above.
		throw new Error(`Unknown file format ${String(format)}`);
	}
	return found;
}

function extensionOf(name: string): string | null {
	const dot = name.lastIndexOf('.');
	return dot > 0 && dot < name.length - 1
		? name.slice(dot).toLowerCase()
		: null;
}

/**
 * The format a file SAYS it is, from the type the browser reports and then its name. Only
 * good for the check a page makes before uploading; the server decides from the content.
 */
export function formFileClaimedFormat(
	file: Pick<FormFileCandidateModel, 'name' | 'type'>,
): FormFileFormatModel | null {
	const type = file.type.split(';')[0]?.trim().toLowerCase() ?? '';
	const byType = FORM_FILE_FORMATS.find(
		(format) =>
			format.contentType === type ||
			(type === 'image/jpg' && format.format === 'jpeg'),
	);
	if (byType != null) {
		return byType;
	}
	// A browser that reports no type (or a generic one) leaves the name to go by.
	const extension = extensionOf(file.name);
	return extension == null
		? null
		: (FORM_FILE_FORMATS.find((format) =>
				format.extensions.includes(extension),
			) ?? null);
}

/**
 * The refusal for a file whose content is none of the accepted formats. When its name or
 * declared type claims a kind the question takes (a PDF, a picture), the refusal says that
 * it is not a valid one of those; otherwise it is simply a kind the question does not take.
 */
export function formFileContentRefusal(
	claimed: Pick<FormFileCandidateModel, 'name' | 'type'> | null | undefined,
	types: ReadonlyArray<FormFileTypeGroup>,
): FormFieldErrorCode {
	const format = claimed != null ? formFileClaimedFormat(claimed) : null;
	if (format == null || !types.includes(format.group)) {
		return 'file_type_not_allowed';
	}
	return format.group === 'pdf' ? 'file_not_valid_pdf' : 'file_not_valid_image';
}

/**
 * The check a page makes before it uploads a file: a kind the question takes, not empty,
 * within the size limit, and room for one more (`alreadyCount` files are attached or on
 * their way). The server checks all of it again, and the kind from the content.
 */
export function checkFormFileBeforeUpload(
	limits: FormFileLimitsModel,
	file: FormFileCandidateModel,
	alreadyCount: number,
): FormFieldErrorCode | null {
	const format = formFileClaimedFormat(file);
	if (format == null || !limits.types.includes(format.group)) {
		return 'file_type_not_allowed';
	}
	if (file.size <= 0) {
		return 'file_empty';
	}
	if (file.size > limits.maxBytes) {
		return 'file_too_large';
	}
	if (alreadyCount >= limits.maxFiles) {
		return 'too_many_files';
	}
	return null;
}

function startsWithBytes(
	bytes: Uint8Array,
	prefix: ReadonlyArray<number>,
): boolean {
	return (
		bytes.length >= prefix.length &&
		prefix.every((byte, index) => bytes[index] === byte)
	);
}

function asciiAt(bytes: Uint8Array, start: number, end: number): string {
	if (bytes.length < end) {
		return '';
	}
	let text = '';
	for (let index = start; index < end; index++) {
		text += String.fromCharCode(bytes[index] ?? 0);
	}
	return text;
}

/**
 * What a file really is, from its first bytes, never its name or the type the browser
 * declared; null for anything that is not one of the accepted formats. An HTML page, an
 * SVG, a program, a zip or an Office document renamed to .pdf or .png is null here, and is
 * refused. A PDF must start with its signature: a reader that tolerates junk before it
 * would let a page smuggle markup in front of a real PDF.
 */
export function sniffFormFile(bytes: Uint8Array): FormFileFormat | null {
	if (asciiAt(bytes, 0, 5) === '%PDF-') {
		return 'pdf';
	}
	if (startsWithBytes(bytes, [0xff, 0xd8, 0xff])) {
		return 'jpeg';
	}
	if (
		startsWithBytes(bytes, [0x89, 0x50, 0x4e, 0x47, 0x0d, 0x0a, 0x1a, 0x0a])
	) {
		return 'png';
	}
	if (asciiAt(bytes, 0, 4) === 'RIFF' && asciiAt(bytes, 8, 12) === 'WEBP') {
		return 'webp';
	}
	return null;
}

// Control characters, and the invisible formatting characters that can make a name read
// differently from what it is ("invoice‮fdp.exe" shows as "invoiceexe.pdf").
// eslint-disable-next-line no-control-regex
const NAME_INVISIBLES = /[\u0000-\u001F\u007F-\u009F؜​-‏‪-‮⁦-⁩﻿]/g;
// Characters a downloaded file may not have in its name on Windows, and quotes, which
// break a Content-Disposition header.
const NAME_RESERVED = /[<>:"|?*]/g;

/**
 * The name a file is kept and shown under: the last part of what the visitor's computer
 * called it, without invisible or reserved characters, ending in an extension that
 * matches what the file really is (a JPEG sent as `scan.png` is kept as `scan.jpg`, and a
 * PDF sent as `form.exe` as `form.exe.pdf`), and at most FORM_FILE_NAME_MAX_LENGTH long.
 */
export function cleanFormFileName(
	name: string,
	format: FormFileFormat,
): string {
	const { extensions } = formFileFormat(format);
	const canonical = extensions[0] ?? '';
	const lastPart = name.split(/[\\/]/).pop() ?? '';
	let cleaned = lastPart
		.normalize('NFC')
		.replace(NAME_INVISIBLES, '')
		.replace(NAME_RESERVED, '_')
		.replace(/\s+/g, ' ')
		.trim()
		.replace(/^[.\s]+/, '')
		.replace(/[.\s]+$/, '');

	const extension = extensionOf(cleaned);
	let base = cleaned;
	let ending = canonical;
	if (extension != null && extensions.includes(extension)) {
		// Already says what it is; keep the visitor's spelling (".JPEG", ".Pdf").
		base = cleaned.slice(0, cleaned.length - extension.length);
		ending = cleaned.slice(cleaned.length - extension.length);
	} else if (
		extension != null &&
		FORM_FILE_FORMATS.some((other) => other.extensions.includes(extension))
	) {
		// Named as another accepted kind: the name is corrected to what the content is.
		base = cleaned.slice(0, cleaned.length - extension.length);
	}
	base = base.replace(/[.\s]+$/, '');
	if (base === '') {
		base = 'file';
	}
	cleaned = `${base.slice(0, FORM_FILE_NAME_MAX_LENGTH - ending.length)}${ending}`;
	return cleaned;
}
